import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "ui/components/card";
import { getCaseStudyDetails } from "../../utils/api"; // adjust path if needed
import { DetailList } from "@/modules/casestudies/components/detail-list";
import { DynamicTable } from "@/modules/casestudies/components/dynamic-table";
import { assetUrl } from "utils/index";
import VideoPlayerModal from "@/modules/casestudies/components/VideoPlayerModal";
import EditItemButton from "components/buttons/edit";
import { NDA_LEVEL } from "../../utils/constant";
import DashboardMenu from "@/components/menu";
import { DropdownMenuItem } from "ui/components/dropdown-menu";
import HeaderPortal from "components/portal/header-portal";
import { Download } from "lucide-react";
import { Button } from "ui/components/button";
import JSZip from "jszip";
import ImageGallery from "components-v2/image-gallery";
import { UserFieldInput } from "permissions/components/user-field-input";
import { useSelector } from "react-redux";
import { selectUser } from "store/src/slices/auth-slice";

export default function ProjectDetails() {
  const [formData, setFormData] = useState(null);
  const user = useSelector(selectUser);
  const [isLoading, setIsLoading] = useState(true);
  const { casestudyId } = useParams();
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    getCaseStudyDetails({ id: casestudyId }).then((data) => {
      setFormData(data);
      setIsLoading(false);
    });
  }, [casestudyId]);

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadAll = async () => {
    if (!formData.screenshots?.length) return;
    setIsDownloading(true);
    try {
      const zip = new JSZip();
      const projectName = formData.name || formData.project?.name || "screenshots";

      await Promise.all(
        formData.screenshots.map(async (id, i) => {
          const response = await fetch(assetUrl(id), { credentials: "include" });
          const blob = await response.blob();
          const ext = blob.type.split("/")[1] || "jpg";
          zip.file(`${projectName}-screenshot-${i + 1}.${ext}`, blob);
        })
      );

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(zipBlob);
      a.download = `${projectName}-screenshots.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading || !formData)
    return (
      <div className="flex justify-center items-center h-full w-full py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900" />
      </div>
    );

  // Table headers
  const basicDetailsHeaders = [
    { key: "label", label: "Field", className: "w-60" },
    { key: "value", label: "Value" },
  ];
  const basicDetailsData = [
    {
      label: "Name",
      value: formData.name,
    },
    {
      label: "Alias",
      value: formData.alias,
    },
    {
      label: "Project",
      value: formData.project?.name,
    },
    {
      label: "Author",
      value: formData.user?.first_name + " " + formData.user?.last_name,
    },
    {
      label: "Start Date",
      value: formData.date_started,
    },
    { label: "Timeframe", value: formData.duration },
    { label: "Team Size", value: formData.team_size },
    {
      label: "Industry",
      value: formData.industry_v1?.type,
    },
    {
      label: "Sub Industry",
      value: formData?.industry_v1?.name,
    },
    {
      label: "NDA Level",
      value: formData.nda_level ? NDA_LEVEL[formData.nda_level]?.label : "",
    },
    { label: "Tag Line", value: formData.tag_line },
  ];

  const techStackHeaders = [
    { key: "name", label: "Technology", className: "w-60" },
    { key: "type", label: "Service" },
  ];
  const toolsHeaders = [
    { key: "name", label: "Name", className: "w-60" },
    { key: "type", label: "Type" },
  ];
  const deliverablesHeaders = [
    { key: "deliverable", label: "Deliverable" },
    { key: "link", label: "Link" },
  ];

  return (
    <div className="max-w-5xl ml-5 p-6 space-y-8">
      <HeaderPortal>
        <UserFieldInput targetItem={casestudyId} field="name" collection="case_studies">
          <EditItemButton />
        </UserFieldInput>
      </HeaderPortal>

      {/* 2. Basic Details Table */}
      <DynamicTable
        title="Basic Details"
        headers={basicDetailsHeaders}
        data={basicDetailsData}
        emptyMessage="No basic details available."
        defaultColumnClassName="font-bold"
      />

      {/* 4. Tech Stack Table */}
      <DynamicTable
        title="Tech Stack"
        headers={techStackHeaders}
        data={formData.tech_stack}
        emptyMessage="No tech stack data available."
        defaultColumnClassName="font-bold"
      />

      {/* 5. Tools Table */}
      <DynamicTable
        title="Tools"
        headers={toolsHeaders}
        data={formData.tools}
        emptyMessage="No tools data available."
        defaultColumnClassName="font-bold"
      />

      {/* 6. Client Requirement */}
      <div>
        <h2 className="text-xl font-bold mb-3">Client Requirement</h2>
        <DetailList text={formData.client_requirement} />
      </div>

      {/* 7. Solution Overview */}
      <div>
        <h2 className="text-xl font-bold mb-3">Solution Overview</h2>
        <DetailList text={formData.solution_overview} />
      </div>

      {/* 8. Obstacles */}
      <div>
        <h2 className="text-xl font-bold mb-3">Obstacles</h2>
        <DetailList text={formData.challenges} />
      </div>

      {/* 9. Achievements */}
      <div>
        <h2 className="text-xl font-bold mb-3">Achievements</h2>
        <DetailList text={formData.achievements} />
      </div>

      {/* 10. Outcomes */}
      <section>
        <h2 className="text-xl font-bold mb-3">Outcomes</h2>
        <div className="flex flex-col gap-5">
          <div>
            <h3 className="font-bold mb-2">Project Highlights</h3>
            <DetailList text={formData.outcomes_project_highlights} />
          </div>
          <div>
            <h3 className="font-bold mb-2">Features</h3>
            <DetailList text={formData.outcomes_features} />
          </div>
        </div>
      </section>

      {/* 11. Deliverables Table */}
      <DynamicTable
        title="Deliverables"
        headers={deliverablesHeaders}
        data={formData.deliverables_list}
        emptyMessage="No deliverables data available."
        defaultColumnClassName="font-bold"
      />

      {/* 12. Methodology */}
      <div>
        <h2 className="text-xl font-bold mb-3">Methodology</h2>
        <DetailList text={formData.methodology} />
      </div>

      {/* 13. Screenshots (horizontal slider) */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold">Screenshots</h2>
          {formData.screenshots?.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadAll}
              disabled={isDownloading}
              className="flex items-center gap-1.5"
            >
              <Download className="w-4 h-4" />
              {isDownloading ? "Downloading..." : "Download All"}
            </Button>
          )}
        </div>
        <ImageGallery
          images={(formData.screenshots || []).map((id) => assetUrl(id))}
        />
      </div>

      {/* 14. Video Preview (single video, open modal player) */}
      {formData.video && (
        <div className="mt-6">
          <h2 className="text-xl font-bold mb-3">Video</h2>
          <div className="flex gap-4 overflow-x-auto pb-2">
            <div
              className="w-32 h-32 min-w-[8rem] border rounded overflow-hidden flex-shrink-0 cursor-pointer flex items-center justify-center bg-black/10 relative"
              onClick={() => setSelectedVideo(assetUrl(formData.video))}
              title="Click to play video"
            >
              <video
                src={assetUrl(formData.video)}
                className="w-full h-full object-cover"
                preload="auto"
                onClick={(e) => e.preventDefault()} // Prevent default video controls in preview
              />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 48 48"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="24" cy="24" r="24" fill="rgba(0,0,0,0.4)" />
                  <polygon points="20,16 36,24 20,32" fill="#fff" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {selectedVideo && (
        <VideoPlayerModal
          videoUrl={selectedVideo}
          onClose={() => setSelectedVideo(null)}
        />
      )}

      {/* 14. Testimonials */}
      <div>
        <h2 className="text-xl font-bold mb-3">Client Testimonials</h2>
        {formData.client_testimonials &&
          formData.client_testimonials.length > 0 ? (
          <div className="grid grid-cols-2 gap-5">
            {formData.client_testimonials.map((testimonial, idx) => (
              <p
                key={idx}
                className="p-4 border rounded-md text-base text-muted-foreground"
              >
                {testimonial}
              </p>
            ))}
          </div>
        ) : (
          <p className="text-base text-muted-foreground">
            No testimonial available.
          </p>
        )}
      </div>
    </div>
  );
}
